<template>
    <div
        class="sl-card sl-card--view-comments"
        :class="{ 'sl-card--no-request': !request }"
        @click="view"
    >
        <div v-if="loading">
            <div class="loader"></div>
        </div>

        <mdi-icon v-else icon="mdiMessageTextOutline" class="sl-card__icon" />

        <teleport to="body">
            <review-request-comments
                v-if="request"
                :is-open="isOpen"
                :request="request"
                read-only
                @close="isOpen = false"
            />
        </teleport>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import constants from '@/constants/constants';
import ReviewRequest from '@/models/ReviewRequest';
import ReviewRequestComments from '@/components/review-requests/ReviewRequestComments.vue';

// ------------------------------------------------------------ PROPS

const props = defineProps({
    order:
    {
        type:     Object,
        required: true,
    },
});

// ------------------------------------------------------------ COMPOSABLES

const store = useStore();
const { t } = useI18n({ useScope: 'global' });
const toast = useToast();

// ------------------------------------------------------------ DATA

const isOpen = ref(false);
const loading = ref(false);
const request = ref(null);

// ------------------------------------------------------------ METHODS

async function view()
{
    if(isOpen.value || !request.value)
    {
        return;
    }

    isOpen.value = true;
}

/**
 * Get the review request attached to an order.
 * If the request already exists in the store, take it from there. Otherwise,
 * fetch it from the back end.
 *
 * @param {number} ref The order's reference number.
 * @returns {ReviewRequest}
 */
async function getRequest(ref)
{
    if(loading.value)
    {
        return;
    }

    loading.value = true;

    // Search the store
    const req = store.state.orders.waitingForReview.find(
        r => r.order.reference === ref
    );
    if(req)
    {
        loading.value = false;
        return req;
    }

    // Fetch the request
    try
    {
        return await ReviewRequest.fetchByOrder(ref);
    }
    catch(error)
    {
        if(error.response?.status !== 404)
        {
            console.error('error retrieving uploads', { error });
            toast.error(t('common.errors.error'));
        }

        return null;
    }
    finally
    {
        loading.value = false;
    }
}

// ------------------------------------------------------------ LIFECYCLE HOOKS

onMounted(async() =>
{
    request.value = await getRequest(props.order.reference);
});
</script>

<style scoped>
@reference "#main.css";

.sl-card--view-comments
{
    @apply cursor-pointer;
}

.sl-card__icon
{
    @apply w-6 h-6 text-primary-500 transition-colors;
}

.sl-card--view-comments:hover .sl-card__icon
{
    @apply text-primary-700;
}

.sl-card--no-request
{
    @apply cursor-default;

    & .sl-card__icon,
    &:hover .sl-card__icon
    {
        @apply text-gray-500;
    }
}
</style>
